/**
 * CAOS-648 — an accessible name that lives inside a shadow root.
 *
 * Several components draw the element a screen reader names — a toolbar, a grid, a tab list, a
 * filter group — inside their own shadow root, and take that name from an attribute on the host.
 * Copying the attribute once, in the constructor or when the element connects, leaves the name stale
 * the moment a page sets it afterwards, and a page assembled from stored metadata always sets its
 * attributes afterwards. Nothing looks wrong on screen; only a screen reader user hears the generic
 * name. So every such component observes the attribute and applies it through here on every change.
 */

/**
 * Name `target` with `value`, or with `fallback` when the attribute is absent; with no fallback the
 * name is removed. An empty value is kept as an empty name rather than replaced by the fallback: the
 * attribute is present, and what it says is the page's decision.
 */
export function applyAccessibleName(target: Element, value: string | null, fallback: string | null): void {
  const name = value ?? fallback;
  if (name === null) target.removeAttribute('aria-label');
  else target.setAttribute('aria-label', name);
}
